/**
 * Connector Dynamic Service
 *
 * Service for fetching dynamic dropdown values from connector APIs.
 * Supports both API Connections (managed connectors like Teams, SharePoint)
 * and Service Provider connectors (Azure Blob, Service Bus).
 *
 * Uses the x-ms-dynamic-list extension patterns from LogicAppsUX.
 */

import { getArmBaseUrl } from './armConfig';
import type { ConnectionReference } from '@/types/connectionReference';
import type { DynamicValuesConfig } from '@/types/operationParameter';
import type { DynamicOption } from './dynamicDataService';
import type { DereferencedSwagger } from '@/parsers/SwaggerParser';
import type { ParameterNameResolver } from './parameterNamingService';
import type { SwaggerParameter } from '@/types/swagger';
import { buildDynamicInvokeUrl, buildResolvedServiceProviderConnectionPayload } from './serviceProviderRuntime';
import { postManagedApiDynamicInvoke } from './dynamicConnectionService';
import { extractServiceProviderName, isServiceProviderConnector } from '@/utils/connections';
import { isTemplateExpression } from './tokenParsingService';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Request parameters for fetching dynamic list values
 */
export interface FetchDynamicListRequest {
  /** Connection reference for the connector */
  connectionReference: ConnectionReference;
  /** Connector/API ID */
  connectorId: string;
  /** Dynamic values configuration from the parameter */
  dynamicConfig: DynamicValuesConfig;
  /** Resolved parameter values (dependencies) */
  resolvedParameters: Record<string, unknown>;
  /** Azure access token */
  accessToken: string;
  /** Connector swagger spec (required for managed API connections to look up operation paths) */
  connectorSwagger?: DereferencedSwagger;
  /** MI token audience (from `useMiAudience`). Required only when `isManagedIdentity` is true. */
  miAudience?: string;
  /** Whether the connection authenticates via MI (from `useManagedConnectionInfo`). */
  isManagedIdentity?: boolean;
  /** Live `connectionRuntimeUrl` from the connection ARM resource (from `useManagedConnectionInfo`). */
  connectionRuntimeUrl?: string;
  /** Bypass the in-memory cache and force a network call (user-initiated refresh). */
  forceRefresh?: boolean;
  /**
   * Whether the designer is in draft mode. Threaded down to app-setting
   * resolution so draft vs published env-settings are read (and cached)
   * independently. Defaults to production when omitted.
   */
  isDraftMode?: boolean;
}

/**
 * Response from dynamic list API
 */
export interface FetchDynamicListResponse {
  /** Fetched options */
  options: DynamicOption[];
  /** Error if fetch failed */
  error?: string;
}

/**
 * Raw response from Azure dynamic list APIs
 */
interface DynamicListApiResponse {
  body?: unknown;
  statusCode?: number;
  headers?: Record<string, string>;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// API version for managed API connection dynamicList (direct to connection resource)
const MANAGED_API_VERSION = '2018-07-01-preview';
// API version for service provider dynamic invoke (via hostruntime)

// Cache for dynamic list results (5 minute TTL)
const dynamicListCache = new Map<string, { options: DynamicOption[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

// =============================================================================
// CONNECTION CONFIGURATION
// =============================================================================

// =============================================================================
// MAIN FETCH FUNCTION
// =============================================================================

/**
 * Fetch dynamic list values from connector API
 *
 * Supports three patterns:
 * 1. API Connections with x-ms-dynamic-list (modern): Uses /dynamicList endpoint
 *    POST https://management.azure.com{connectionId}/dynamicList
 * 2. API Connections with x-ms-dynamic-values (legacy): Uses /dynamicInvoke endpoint
 *    POST https://management.azure.com{connectionId}/dynamicInvoke
 * 3. Service Providers: Uses dynamicInvoke via hostruntime
 *    POST /runtime/webhooks/workflow/api/management/operationGroups/{connectorId}/operations/{operationId}/dynamicInvoke
 */
export async function fetchDynamicListValues(
  request: FetchDynamicListRequest
): Promise<FetchDynamicListResponse> {
  const { connectionReference, connectorId, dynamicConfig, resolvedParameters, accessToken, connectorSwagger, miAudience, isManagedIdentity, connectionRuntimeUrl, forceRefresh, isDraftMode = false } = request;
  const connectionId = connectionReference.connection.id;
  const cacheKey = buildCacheKey(connectionId, dynamicConfig.operationId, resolvedParameters, {
    isManagedIdentity,
    miAudience,
    connectionRuntimeUrl,
  }) + `::draft:${isDraftMode}`;
  if (!forceRefresh) {
    const cached = dynamicListCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return { options: cached.options };
    }
  }

  try {
    // Determine connector type and call appropriate API
    const isServiceProvider = isServiceProviderConnector(connectorId);

    let response: DynamicListApiResponse;
    if (isServiceProvider) {
      response = await callServiceProviderDynamicApi(
        connectorId,
        dynamicConfig,
        resolvedParameters,
        connectionReference,
        accessToken,
        isDraftMode
      );
    } else if (dynamicConfig.isLegacy) {
      // Legacy x-ms-dynamic-values uses /dynamicInvoke with path-based request
      response = await callManagedApiDynamicInvoke(
        connectionReference,
        dynamicConfig,
        resolvedParameters,
        accessToken,
        connectorSwagger,
        miAudience,
        isManagedIdentity,
        connectionRuntimeUrl
      );
    } else {
      // Modern x-ms-dynamic-list uses /dynamicList
      response = await callManagedApiDynamicList(
        connectionReference,
        connectorId,
        dynamicConfig,
        resolvedParameters,
        accessToken,
        connectorSwagger
      );
    }

    // Unwrap dynamicInvoke response if needed (it returns { response: { body: ... } })
    // while dynamicList returns { body: ... } directly
    const actualResponse = (response as { response?: DynamicListApiResponse }).response ?? response;

    // Parse response into options (with pagination support for large result sets)
    const options = await parseResponseToOptionsWithPagination(actualResponse, dynamicConfig, accessToken);

    // Cache the result
    dynamicListCache.set(cacheKey, { options, timestamp: Date.now() });

    return { options };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch dynamic options';
    console.error('Dynamic list fetch error:', error);
    return { options: [], error: message };
  }
}

// =============================================================================
// API CALL IMPLEMENTATIONS
// =============================================================================

/**
 * Call managed API connection's dynamicInvoke endpoint (for legacy x-ms-dynamic-values).
 *
 * URL/body routing (MI vs legacy) and the actual POST are handled in
 * {@link postManagedApiDynamicInvoke}; this function only resolves the
 * swagger operation and shapes the `request` envelope.
 */
async function callManagedApiDynamicInvoke(
  connectionReference: ConnectionReference,
  dynamicConfig: DynamicValuesConfig,
  resolvedParameters: Record<string, unknown>,
  accessToken: string,
  connectorSwagger: DereferencedSwagger | undefined,
  miAudience: string | undefined,
  isManagedIdentity: boolean | undefined,
  connectionRuntimeUrl: string | undefined
): Promise<DynamicListApiResponse> {
  const operationDetails = findOperationInSwagger(connectorSwagger, dynamicConfig.operationId);
  if (!operationDetails) {
    throw new Error(`Operation '${dynamicConfig.operationId}' not found in connector swagger`);
  }

  return postManagedApiDynamicInvoke<DynamicListApiResponse>({
    connectionReference,
    request: buildDynamicInvokeRequest(operationDetails, resolvedParameters),
    accessToken,
    miAudience,
    isManagedIdentity,
    connectionRuntimeUrl,
    label: 'list',
  });
}

/**
 * Find an operation in the swagger by operationId.
 *
 * Exported so other services (e.g. dynamicTreeService) can build the same
 * `request: { method, path, queries }` shape required by the Otto control
 * plane's `/dynamicInvoke` endpoint.
 *
 * Returns the parameter list as well so callers can honor per-parameter
 * extensions like `x-ms-url-encoding` when substituting values into the
 * path template.
 */
export function findOperationInSwagger(
  swagger: DereferencedSwagger | undefined,
  operationId: string
): { method: string; path: string; parameters?: SwaggerParameter[] } | undefined {
  if (!swagger?.paths) {
    return undefined;
  }

  for (const [path, pathItem] of Object.entries(swagger.paths)) {
    if (!pathItem) continue;

    for (const method of ['get', 'post', 'put', 'patch', 'delete'] as const) {
      const operation = pathItem[method];
      if (operation?.operationId === operationId) {
        // OpenAPI 2.0 allows path-level parameters as well, but the
        // managed-connector swaggers we consume always declare path
        // parameters (including `connectionId`) at the operation level.
        // Returning the operation's parameters is sufficient for resolving
        // per-parameter extensions like `x-ms-url-encoding`.
        const result: { method: string; path: string; parameters?: SwaggerParameter[] } = { method, path };
        if (operation.parameters?.length) {
          result.parameters = operation.parameters;
        }
        return result;
      }
    }
  }

  return undefined;
}

/**
 * Encode a path-parameter value the number of times specified by
 * `x-ms-url-encoding`. Mirrors LogicAppsUX `encodePathValue`: `'double'` →
 * `encodeURIComponent(encodeURIComponent(v))` (used by SharePoint/Outlook
 * pickers whose dataset id is itself a URL); anything else → single
 * `encodeURIComponent`.
 */
export function encodePathValueForExtension(
  value: string,
  encoding: 'single' | 'double' | undefined
): string {
  const count = encoding === 'double' ? 2 : 1;
  let encoded = value;
  for (let i = 0; i < count; i++) {
    encoded = encodeURIComponent(encoded);
  }
  return encoded;
}

/**
 * Build the `request` payload for the Otto control plane's `/dynamicInvoke`
 * endpoint from a swagger operation. Strips the `{connectionId}` placeholder
 * (the connection is encoded in the URL), substitutes the remaining
 * `{paramName}` placeholders using `x-ms-url-encoding`-aware encoding, and
 * collects leftover parameters as query-string entries.
 *
 * Throws when the path still contains unsubstituted placeholders, so callers
 * fail loudly instead of POSTing a malformed URL.
 */
export function buildDynamicInvokeRequest(
  operation: { method: string; path: string; parameters?: SwaggerParameter[] },
  parameters: Record<string, unknown>
): { method: string; path: string; queries?: Record<string, string> } {
  let path = operation.path.replace('{connectionId}', '').replace('//', '/');
  const queries: Record<string, string> = {};

  for (const [name, raw] of Object.entries(parameters)) {
    const value = String(raw ?? '');
    // Escape regex metacharacters in the parameter name to prevent regex
    // injection from connector-supplied identifiers.
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const placeholder = new RegExp(`\\{${escaped}\\}`, 'gi');

    if (placeholder.test(path)) {
      const spec = operation.parameters?.find((p) => p.in === 'path' && p.name === name);
      path = path.replace(placeholder, encodePathValueForExtension(value, spec?.['x-ms-url-encoding']));
    } else if (value) {
      queries[name] = value;
    }
  }

  const remaining = path.match(/\{[^}]+\}/g);
  if (remaining?.length) {
    throw new Error(`Missing required path parameters: ${remaining.join(', ')}`);
  }

  return {
    method: operation.method.toUpperCase(),
    path,
    ...(Object.keys(queries).length > 0 && { queries }),
  };
}

/**
 * Call managed API connection's dynamicList endpoint
 *
 * For managed API connections (Teams, SharePoint, etc.) with x-ms-dynamic-list,
 * calls directly to the connection resource's dynamicList endpoint:
 * POST https://management.azure.com{connectionId}/dynamicList?api-version=2018-07-01-preview
 *
 * The request body contains:
 * - dynamicInvocationDefinition: The dynamic config (operationId, itemsPath, etc.)
 * - parameters: Resolved parameter values
 * - configuration: Connection configuration for authentication context
 *
 * This follows the pattern from LogicAppsUX ConsumptionConnectorService.getListDynamicValues()
 */
async function callManagedApiDynamicList(
  connectionReference: ConnectionReference,
  _connectorId: string,
  dynamicConfig: DynamicValuesConfig,
  resolvedParameters: Record<string, unknown>,
  accessToken: string,
  _connectorSwagger: DereferencedSwagger | undefined
): Promise<DynamicListApiResponse> {
  const connectionId = connectionReference.connection.id;

  // Use dynamicList endpoint (not dynamicInvoke) for x-ms-dynamic-list
  const url = `${getArmBaseUrl()}${connectionId}/dynamicList?api-version=${MANAGED_API_VERSION}`;

  // Build dynamicInvocationDefinition from the dynamicConfig
  // This contains the operationId and item path configuration
  const dynamicInvocationDefinition = {
    operationId: dynamicConfig.operationId,
    itemsPath: dynamicConfig.itemsPath,
    itemValuePath: dynamicConfig.itemValuePath,
    itemTitlePath: dynamicConfig.itemTitlePath,
    ...(dynamicConfig.itemDescriptionPath && { itemDescriptionPath: dynamicConfig.itemDescriptionPath }),
    ...(dynamicConfig.itemSelectablePath && { itemSelectablePath: dynamicConfig.itemSelectablePath }),
    // Include the parameter configuration for the server to understand dependencies
    ...(dynamicConfig.parameters && { parameters: dynamicConfig.parameters }),
  };

  // Build request body - following LogicAppsUX ConsumptionConnectorService.getListDynamicValues pattern
  // The API expects { dynamicInvocationDefinition, parameters, properties? }
  // NOT { ..., configuration } - that property is not accepted
  // 'properties' is only used for managed identity connections (workflowReference)
  const body = {
    dynamicInvocationDefinition,
    parameters: resolvedParameters,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Dynamic list API failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

/**
 * Call service provider connector's dynamicInvoke endpoint via the Otto data plane.
 *
 * Cloud:  POST {logicEndpoint}/automationProjects/{p}/applications/{appName}/operationGroups/{provider}/operations/{op}/dynamicInvoke?api-version={otto}
 * Local:  POST {localRuntimeUrl}/runtime/webhooks/workflow/api/management/operationGroups/{provider}/operations/{op}/dynamicInvoke?api-version=2024-02-01
 */
async function callServiceProviderDynamicApi(
  connectorId: string,
  dynamicConfig: DynamicValuesConfig,
  resolvedParameters: Record<string, unknown>,
  connectionReference: ConnectionReference,
  accessToken: string,
  isDraftMode: boolean
): Promise<DynamicListApiResponse> {
  // Extract the service provider name from connector ID
  // Format: /serviceProviders/AzureBlob -> AzureBlob
  const providerName = extractServiceProviderName(connectorId);

  const url = buildDynamicInvokeUrl(providerName, dynamicConfig.operationId);

  // Build the configuration.connection payload to match what the runtime expects.
  // Service-provider connections have no separate connection resource — the runtime
  // needs the actual parameterValues / parameterSetName / serviceProvider id stored
  // on the connection reference, mirroring connections.json.
  // Logic Apps Standard payload shape:
  //   { parameters, configuration: { connection: { parameterValues, parameterSetName, serviceProvider: { id } } } }
  //
  // App-setting references (`@appsetting('NAME')`) are inlined to their literal
  // values inside the builder. The dynamicInvoke runtime does not evaluate
  // workflow expressions, so secrets stored in app settings must be resolved
  // client-side or the SP reports the parameter as missing.
  const connectionPayload = await buildResolvedServiceProviderConnectionPayload(
    connectorId,
    connectionReference,
    accessToken,
    isDraftMode,
  );

  const body = {
    parameters: resolvedParameters,
    configuration: {
      connection: connectionPayload,
    },
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[DynamicList] service provider HTTP error', {
      url,
      status: response.status,
      body: errorText,
    });
    throw new Error(`Service provider dynamic API failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

// =============================================================================
// PAGINATION SUPPORT
// =============================================================================

/**
 * Response from paginated fetch operations
 */
interface PaginatedResponse {
  /** All fetched values combined */
  values: unknown[];
  /** Whether pagination was incomplete (e.g., due to error) */
  incomplete: boolean;
  /** Error message if pagination failed */
  error?: string;
}

// Maximum pages to fetch during pagination to prevent infinite loops or memory issues
const MAX_PAGINATION_PAGES = 10;

/**
 * Fetch all pages of a paginated response
 *
 * Follows nextLink/@odata.nextLink for OData-style pagination.
 * This handles responses with more than 100 items (common limit).
 *
 * @param initialData - Initial response data from first API call
 * @param headers - Headers to include in pagination requests
 * @returns Combined values from all pages
 */
async function fetchAllPaginatedValues(
  initialData: Record<string, unknown>,
  headers: Record<string, string>
): Promise<PaginatedResponse> {
  // Extract initial values - handles both 'value' (OData) and direct array
  const allValues: unknown[] = Array.isArray(initialData.value)
    ? [...initialData.value]
    : Array.isArray(initialData)
      ? [...initialData]
      : [];

  // Get next page link (OData standard formats)
  let nextLink = (initialData.nextLink ?? initialData['@odata.nextLink']) as string | undefined;
  let incomplete = false;
  let lastError: string | undefined;
  let pageCount = 0;

  // Follow pagination links (with safeguard against infinite loops)
  while (nextLink && pageCount < MAX_PAGINATION_PAGES) {
    pageCount++;
    try {
      const response = await fetch(nextLink, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        incomplete = true;
        lastError = `Pagination request failed (${response.status})`;
        break;
      }

      const pageData = await response.json() as Record<string, unknown>;

      // Add values from this page
      if (Array.isArray(pageData.value)) {
        allValues.push(...pageData.value);
      }

      // Get next link
      nextLink = (pageData.nextLink ?? pageData['@odata.nextLink']) as string | undefined;
    } catch (error) {
      incomplete = true;
      lastError = error instanceof Error ? error.message : String(error);
      break; // Graceful degradation - return partial results
    }
  }

  // Check if we hit the page limit
  if (nextLink && pageCount >= MAX_PAGINATION_PAGES) {
    console.warn(`[fetchAllPaginatedValues] Pagination capped at ${MAX_PAGINATION_PAGES} pages`);
    incomplete = true;
  }

  // Build result, only including error if it's defined
  const result: PaginatedResponse = { values: allValues, incomplete };
  if (lastError) {
    result.error = lastError;
  }
  return result;
}

/**
 * Check if response has pagination (nextLink)
 */
function hasPagination(data: unknown): data is Record<string, unknown> & { nextLink?: string; '@odata.nextLink'?: string } {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return !!(obj.nextLink || obj['@odata.nextLink']);
}

/** Type guard for plain object records (excludes arrays and null). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

/**
 * Parse API response into DynamicOption array
 * Handles pagination for large result sets (100+ items)
 */
async function parseResponseToOptionsWithPagination(
  response: DynamicListApiResponse,
  config: DynamicValuesConfig,
  accessToken: string
): Promise<DynamicOption[]> {

  // Response body contains the data
  let data: unknown = response.body ?? response;

  // Navigate to items using itemsPath
  if (config.itemsPath) {
    data = getValueByPath(data, config.itemsPath) ?? data;
  }

  // Check for pagination (OData nextLink pattern)
  if (hasPagination(data)) {
    const paginated = await fetchAllPaginatedValues(
      data,
      { Authorization: `Bearer ${accessToken}` }
    );

    if (paginated.incomplete) {
      console.warn(`[parseResponseToOptions] Pagination incomplete: ${paginated.error}`);
    }

    return mapItemsToOptions(paginated.values, config);
  }

  // Ensure we have an array
  if (!Array.isArray(data)) {
    console.warn('[parseResponseToOptions] Response is not an array:', data);
    return [];
  }

  return mapItemsToOptions(data, config);
}

/**
 * Map raw items array to DynamicOption array
 */
function mapItemsToOptions(
  items: unknown[],
  config: DynamicValuesConfig
): DynamicOption[] {

  return items.map((item: unknown) => {
    // Path priority for value/title:
    //   1. Explicit itemValuePath / itemTitlePath from the manifest
    //   2. Conventional `value` / `displayName` keys (used by service-provider
    //      dynamicInvoke responses, e.g. Service Bus getEntities)
    //   3. The raw item itself (last-resort, e.g. arrays of strings)
    // NOTE: `getValueByPath(item, undefined)` returns `item` (not undefined), so
    // we must check whether a path was explicitly provided before using it.
    const rawValue = config.itemValuePath
      ? getValueByPath(item, config.itemValuePath)
      : (isRecord(item) ? item.value ?? item : item);
    const rawTitle = config.itemTitlePath
      ? getValueByPath(item, config.itemTitlePath)
      : (isRecord(item) ? item.displayName ?? item.name ?? rawValue : rawValue);
    const option: DynamicOption = {
      value: String(rawValue),
      label: String(rawTitle),
    };

    // Add description if path specified
    if (config.itemDescriptionPath) {
      const desc = getValueByPath(item, config.itemDescriptionPath);
      if (desc !== undefined) {
        option.description = String(desc);
      }
    }

    // Check if item is selectable
    if (config.itemSelectablePath) {
      const selectable = getValueByPath(item, config.itemSelectablePath);
      if (selectable === false) {
        option.disabled = true;
      }
    }

    return option;
  });
}

/**
 * Get value from object by dot-notation path
 */
function getValueByPath(obj: unknown, path: string | undefined): unknown {
  if (!path || obj === null || obj === undefined) {
    return obj;
  }

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Build cache key for dynamic list results
 * Uses sorted keys to ensure consistent cache hits regardless of object property order.
 * MI routing fields are part of the key so a re-classification (e.g. once
 * `useManagedConnectionInfo` settles) produces a cache miss instead of
 * returning a stale bare-route response.
 */
function buildCacheKey(
  connectionId: string,
  operationId: string,
  parameters: Record<string, unknown>,
  miRouting: { isManagedIdentity?: boolean | undefined; miAudience?: string | undefined; connectionRuntimeUrl?: string | undefined }
): string {
  const sortedParams = JSON.stringify(parameters, Object.keys(parameters).sort());
  const mi = `${miRouting.isManagedIdentity ?? false}::${miRouting.miAudience ?? ''}::${miRouting.connectionRuntimeUrl ?? ''}`;
  return `${connectionId}::${operationId}::${sortedParams}::${mi}`;
}

/**
 * Clear the dynamic list cache
 */
export function clearDynamicListCache(): void {
  dynamicListCache.clear();
}

/**
 * Clear cache entries for a specific connection
 */
export function clearCacheForConnection(connectionId: string): void {
  for (const key of dynamicListCache.keys()) {
    if (key.startsWith(connectionId)) {
      dynamicListCache.delete(key);
    }
  }
}

// =============================================================================
// DEPENDENCY RESOLUTION
// =============================================================================

/**
 * Resolve parameter dependencies from current parameter values
 *
 * Given a DynamicValuesConfig with parameter references, resolve the actual
 * values from the current parameter state.
 *
 * @param config - Dynamic values configuration
 * @param parameterValues - Current values of all parameters keyed by parameter name
 * @returns Resolved parameters ready for API call, or null if dependencies not met
 */
/**
 * Look up a parameter value using the ParameterNameResolver (preferred) or fallback strategies.
 *
 * Resolution order:
 * 1. Direct match by parameter name
 * 2. ParameterNameResolver lookup (if provided) - uses canonical name mapping
 * 3. Case-insensitive match (fallback)
 * 4. Swagger x-ms-summary mapping (legacy fallback)
 * 5. Nested path matching (e.g., "groupId" matches "recipient.groupId")
 *
 * @param parameterValues - Current parameter values keyed by parameter name
 * @param reference - The parameter reference to look up (e.g., "PostAs")
 * @param resolverOrMapping - ParameterNameResolver (preferred) or Map for legacy support
 */
function lookupParameterValue(
  parameterValues: Record<string, unknown>,
  reference: string,
  resolverOrMapping?: ParameterNameResolver | Map<string, string>
): unknown {
  // 1. Direct lookup (fastest path)
  if (reference in parameterValues) {
    return parameterValues[reference];
  }

  // 2. Use ParameterNameResolver if provided (preferred path)
  if (resolverOrMapping && 'resolve' in resolverOrMapping) {
    const resolver = resolverOrMapping as ParameterNameResolver;
    const result = resolver.resolve(reference);
    if (result) {
      // Try direct lookup with canonical name
      if (result.canonicalName in parameterValues) {
        return parameterValues[result.canonicalName];
      }
      // Try case-insensitive lookup with canonical name
      const lowerCanonical = result.canonicalName.toLowerCase();
      for (const [key, value] of Object.entries(parameterValues)) {
        if (key.toLowerCase() === lowerCanonical) {
          return value;
        }
      }
    }
  }

  // 3. Case-insensitive fallback
  const lowerRef = reference.toLowerCase();
  for (const [key, value] of Object.entries(parameterValues)) {
    if (key.toLowerCase() === lowerRef) {
      return value;
    }
  }

  // 4. Legacy swagger mapping support (when Map is passed instead of resolver)
  if (resolverOrMapping && resolverOrMapping instanceof Map) {
    const swaggerMapping = resolverOrMapping as Map<string, string>;
    const mappedName = swaggerMapping.get(lowerRef);
    if (mappedName) {
      // Try direct lookup with mapped name
      if (mappedName in parameterValues) {
        return parameterValues[mappedName];
      }
      // Try case-insensitive lookup with mapped name
      const lowerMapped = mappedName.toLowerCase();
      for (const [key, value] of Object.entries(parameterValues)) {
        if (key.toLowerCase() === lowerMapped) {
          return value;
        }
      }
    }
  }

  // 5. Nested path matching (for body parameters like "recipient.groupId")
  for (const [key, value] of Object.entries(parameterValues)) {
    const keyLower = key.toLowerCase();
    // Check if key ends with the reference (e.g., "recipient.groupId" ends with "groupId")
    if (keyLower.endsWith('.' + lowerRef)) {
      return value;
    }
    // Also check if the last segment matches (handles "body.recipient.groupId" -> "groupId")
    const lastSegment = key.split('.').pop()?.toLowerCase();
    if (lastSegment === lowerRef) {
      return value;
    }
  }

  // 6. Body-prefix strip. Otto's swagger parser names body params without the
  // `body.` prefix (`recipient.groupId`), but x-ms-dynamic-list configs and
  // in-editor writes commonly reference them as `body.recipient.groupId`.
  // Strip the prefix and retry the direct + case-insensitive match.
  if (lowerRef.startsWith('body.')) {
    return lookupParameterValue(parameterValues, reference.slice('body.'.length), resolverOrMapping);
  }

  return undefined;
}

/**
 * Determine if an unresolved parameterReference is likely a static value
 * rather than an actual parameter name.
 *
 * Some connectors (e.g., Teams) misuse `{ parameter: "isDefined" }` in
 * x-ms-dynamic-values to pass static filter values like "isDefined", "all",
 * "none", etc. These are NOT references to other operation parameters.
 *
 * Heuristic order (most authoritative first):
 *   1. If `targetOperationParameterNames` is supplied and the reference matches
 *      a real parameter on the dynamic-values target operation, it's a real
 *      reference — gate the call instead of substituting the literal. This
 *      prevents bugs like SharePoint sending `/datasets/dataset/tables`
 *      (literal "dataset") when the Site Address is still empty.
 *   2. If the reference contains dots, it looks like a real parameter path.
 *   3. If any current parameter key matches, it's a real reference.
 *   4. Otherwise, treat as a static value.
 */
function isLikelyStaticValue(
  reference: string,
  parameterValues: Record<string, unknown>,
  targetOperationParameterNames?: Set<string>
): boolean {
  const lowerRef = reference.toLowerCase();

  // (1) Authoritative check: if the target operation has a real param with this
  // name, treat as a real reference (currently unset).
  if (targetOperationParameterNames?.has(lowerRef)) {
    return false;
  }

  // (2) Path-like reference — always real.
  if (reference.includes('.')) {
    return false;
  }

  // (3) Check if any parameter key contains or matches this reference (case-insensitive)
  for (const key of Object.keys(parameterValues)) {
    const lowerKey = key.toLowerCase();
    // Skip internal/metadata keys
    if (lowerKey.startsWith('_')) continue;
    // If a parameter name matches or contains the reference, it's likely a real param
    if (lowerKey === lowerRef || lowerKey.endsWith('.' + lowerRef)) {
      return false;
    }
  }

  // No matching parameter found — treat as static value
  return true;
}

export function resolveDynamicParameters(
  config: DynamicValuesConfig,
  parameterValues: Record<string, unknown>,
  resolverOrMapping?: ParameterNameResolver | Map<string, string>,
  targetOperationParameters?: SwaggerParameter[]
): Record<string, unknown> | null {
  if (!config.parameters) {
    return {};
  }
  const resolved: Record<string, unknown> = {};
  const missing: string[] = [];

  const targetOpParamNames = targetOperationParameters
    ? new Set(targetOperationParameters.map((p) => p.name.toLowerCase()))
    : undefined;

  for (const [key, param] of Object.entries(config.parameters)) {
    if (param.value !== undefined) {
      // Static value
      resolved[key] = param.value;
    } else if (param.parameterReference) {
      // Reference to another parameter - use resolver or mapping for lookup
      const refValue = lookupParameterValue(parameterValues, param.parameterReference, resolverOrMapping);
      if (refValue === undefined || refValue === null || refValue === '') {
        // Some connectors (e.g., Teams) misuse { parameter: "isDefined" } to pass static
        // filter values rather than referencing actual operation parameters. When a reference
        // can't be resolved AND it doesn't look like a real parameter name (no dots, no body
        // prefix, not in swagger path params), treat the reference string as a static value.
        if (isLikelyStaticValue(param.parameterReference, parameterValues, targetOpParamNames)) {
          resolved[key] = param.parameterReference;
        } else if (param.required !== false) {
          missing.push(param.parameterReference);
        }
      } else {
        resolved[key] = refValue;
      }
    }
  }

  if (missing.length > 0) {
    return null;
  }

  return resolved;
}

/**
 * Names of referenced parameters whose resolved value is a runtime expression
 * (e.g. `${agentParameters('buildId')}`). Such values can't be evaluated at
 * design time, so a dynamic list that depends on them must not be fetched.
 */
export function getRuntimeExpressionDependencies(
  config: DynamicValuesConfig,
  resolvedParams: Record<string, unknown> | null,
): string[] {
  if (!config.parameters || !resolvedParams) return [];
  const runtimeDependencies: string[] = [];
  for (const [key, param] of Object.entries(config.parameters)) {
    if (param.value !== undefined || !param.parameterReference) continue;
    const resolvedValue = resolvedParams[key];
    if (typeof resolvedValue === 'string' && isTemplateExpression(resolvedValue)) {
      runtimeDependencies.push(param.parameterReference);
    }
  }
  return runtimeDependencies;
}
/**
 * Get list of parameter names that this dynamic config depends on
 */
export function getDependencyParameterNames(config: DynamicValuesConfig): string[] {
  if (!config.parameters) {
    return [];
  }

  const deps: string[] = [];
  for (const param of Object.values(config.parameters)) {
    if (param.parameterReference) {
      deps.push(param.parameterReference);
    }
  }

  return deps;
}

/**
 * Check if all dependencies are satisfied
 */
export function areDependenciesSatisfied(
  config: DynamicValuesConfig,
  parameterValues: Record<string, unknown>
): boolean {
  return resolveDynamicParameters(config, parameterValues) !== null;
}
